/**
 * "Ask [agent] for these N" on the items list's bulk bar (O2, the bulk half;
 * docs/9-exploration/PROPOSALS-ai-made-reports-agents-and-orchestration-2026-10-16.md §5.2).
 *
 * Pure: given the workspace's template agents and what is known about the
 * viewer, it says which agents can be asked about the chosen items, the line
 * under each that says who will decide what the agent proposes, and — when no
 * agent can be asked — the one line naming the fix and who holds it.
 *
 * Rules it keeps (build contract §2.8):
 *   · an agent that cannot run is not listed; nothing is drawn disabled
 *   · an agent that needs a model is listed only once a model is known to
 *     answer (useAiControl); while that is being checked it is not listed and
 *     no line is shown
 *   · when none can run, one line names the fix, never an internal word; for a
 *     missing model it is the one shared line (AskAiNeedsKey)
 *   · "you decide" only when it is true for the viewer: the agent's reviewer
 *     rule (agent_reviewer_allowed, migration 923) and, for a value, the
 *     permission to accept values (values.accept)
 *   · the value's AI setting (migration 920) is read, never written
 */

import { agentPlainName, type AgentBehaviorKind, type AgentDefinition } from './ai/agentDefinitions';
import { runsOverChosenItems } from './agentTemplateRunners';
import { resolveTriggers } from '../components/agents/template/resolve';
import { getJobRole } from '../types/prioritization/jobRoles';
import type { AgentSettings } from './agentSettingsService';
import { VALUE_AI_MODE_LABEL, type ValueAiSetting } from './valueStatus';
import type { BatchStats } from './agentRunService';

export const AGENTS_SETTINGS_HREF = '/prioritization/settings/agents';

export interface BulkAskAgent {
  def: AgentDefinition;
  settings: AgentSettings | null;
}

export interface BulkAskViewer {
  /** How many rows are chosen. */
  count: number;
  /** The workspace's plan includes AI (isWorkspaceEntitledToAi). */
  entitled: boolean;
  /**
   * Whether an AI control may be drawn (useAiControl): `drawable` once a model is
   * known to answer; `needsKey` once it is known that none will (or the check
   * could not be made). Neither while the check runs.
   */
  ai: { drawable: boolean; needsKey: boolean };
  /** The viewer may switch agents on (admin.settings). */
  isAdmin: boolean;
  /** Names of the workspace's administrators, for "An admin (Wes, Priya) can…". */
  adminNames: string[];
  /** The viewer holds values.accept. */
  mayAcceptValues: boolean;
  /** The agent's reviewer rule, for the viewer; undefined while not known. */
  reviewerAllowed: (agentType: string) => boolean | undefined;
  /** Every value's AI setting in the workspace. */
  valueSettings: ValueAiSetting[];
  /** The label a person reads for a value ("Effort"). */
  valueLabel: (attributeKey: string) => string;
  /** A person's name, or null when unknown. */
  personName: (userId: string | null | undefined) => string | null;
}

/**
 * What one run does with the chosen rows, for the words a person reads:
 * items — one answer per item (Classifier); estimates — one per item and value
 * (Value Estimator); compare — the chosen items against each other, never the
 * rest of the workspace (Duplicate finder, Relationship finder).
 */
export type BulkAskWork = 'items' | 'estimates' | 'compare';

export interface BulkAskEntry {
  agentType: string;
  /** The agent's plain name ("Duplicate finder"). */
  name: string;
  /** "Ask Value Estimator for these 14", "Ask Duplicate finder to compare these 14 with each other". */
  label: string;
  /** Who decides what it proposes, one line per value or output: "Effort: AI recommends; you decide". */
  decides: string[];
  /** Set when more rows are chosen than one run reads: says which it reads. */
  note: string | null;
  work: BulkAskWork;
  /** The most items one run reads (the agent's per-run limit). */
  cap: number;
  def: AgentDefinition;
}

export interface BulkAskFix {
  text: string;
  link?: { label: string; href: string };
}

export interface BulkAskPlan {
  entries: BulkAskEntry[];
  /** Set only when no agent can be asked, for any reason but a missing model. */
  fix: BulkAskFix | null;
  /** No agent can be asked because no model answers: the bar draws the one shared line (AskAiNeedsKey). */
  needsKey: boolean;
}

/** `checking`: whether a model answers is not known yet — nothing is offered and nothing is said. */
type Blocker = 'plan' | 'checking' | 'no_key' | 'off' | 'not_bulk' | 'no_values';
const BLOCKER_ORDER: Blocker[] = ['plan', 'checking', 'no_key', 'off', 'not_bulk', 'no_values'];

/** What an agent other than a value estimator proposes, in a word a person reads. */
const OUTPUT_NOUN: Partial<Record<AgentBehaviorKind, string>> = {
  classify: 'Category',
  detect_cluster: 'Duplicates',
  propose_relationship: 'Relationships',
};

function workOf(def: AgentDefinition): BulkAskWork {
  if (def.behaviorKind === 'estimate_value') return 'estimates';
  if (def.behaviorKind === 'detect_cluster' || def.behaviorKind === 'propose_relationship') return 'compare';
  return 'items';
}

/** The most items one run reads: the agent's own limit, else its definition's. */
function capOf({ def, settings }: BulkAskAgent): number {
  const set = Number((settings?.limits as { perRunObjectCap?: unknown } | undefined)?.perRunObjectCap);
  return Number.isFinite(set) && set > 0 ? set : def.limits.perRunObjectCap;
}

function entryLabel(name: string, work: BulkAskWork, count: number, cap: number): string {
  const which = count > cap ? `${cap} of these ${count}` : `these ${count}`;
  return work === 'compare' ? `Ask ${name} to compare ${which} with each other` : `Ask ${name} for ${which}`;
}

function valuesFor(agentType: string, settings: ValueAiSetting[]): ValueAiSetting[] {
  return settings.filter((v) => v.mode !== 'off' && v.agentType === agentType);
}

// reuse-ack: mirrors src/hooks/useValueAgents.ts agentFor — that one asks whether one value's agent can run on one item (the single-item trigger); this one asks it per agent for many items (the bulk trigger, resolved by the template). Both fold into O2's server readiness function when the task lane lands (R30).
function blockerFor({ def, settings }: BulkAskAgent, viewer: BulkAskViewer): Blocker | null {
  const usesModel = def.evidenceModel !== 'deterministic_only';
  if (usesModel && !viewer.entitled) return 'plan';
  if (usesModel && viewer.ai.needsKey) return 'no_key';
  if (usesModel && !viewer.ai.drawable) return 'checking';
  if (!settings?.enabled) return 'off';
  if (!resolveTriggers(def, settings).manualBulk) return 'not_bulk';
  if (def.behaviorKind === 'estimate_value' && valuesFor(def.agentType, viewer.valueSettings).length === 0) return 'no_values';
  return null;
}

/** "item managers", "item managers or relationship curators"; empty when the agent names no review role. */
export function reviewersPhrase(roles: readonly string[]): string {
  const words = [...new Set(roles)].map((slug) => {
    const label = (getJobRole(slug)?.displayName ?? slug.replace(/[-_]+/g, ' ')).toLowerCase();
    return label.endsWith('s') ? label : `${label}s`;
  });
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} or ${words[words.length - 1]}`;
}

function decidesLines({ def, settings }: BulkAskAgent, viewer: BulkAskViewer): string[] {
  // The database's rule: no review roles stored means anyone here may review (agent_reviewer_allowed).
  const reviewers = reviewersPhrase(settings?.reviewRoles ?? []);
  const reviewer = viewer.reviewerAllowed(def.agentType) === true;

  if (def.behaviorKind === 'estimate_value') {
    // "item managers decide" would read as "you" to an item manager who may not accept values,
    // so that viewer is told the narrower truth.
    const decider = reviewer && viewer.mayAcceptValues
      ? 'you decide'
      : reviewer && reviewers
        ? `${reviewers} who may accept values decide`
        : `${reviewers || 'people who may accept values'} decide`;
    return valuesFor(def.agentType, viewer.valueSettings)
      .map((v) => ({ v, label: viewer.valueLabel(v.attributeKey) }))
      .sort((a, b) => a.label.localeCompare(b.label))
      .map(({ v, label }) => {
        if (v.mode === 'provide') {
          const by = viewer.personName(v.setBy);
          return by ? `${label}: ${VALUE_AI_MODE_LABEL.provide}, set by ${by}` : `${label}: ${VALUE_AI_MODE_LABEL.provide}`;
        }
        return `${label}: ${VALUE_AI_MODE_LABEL.recommend}; ${decider}`;
      });
  }

  // Nothing but a value may be AI provides (R27): every other output waits for a person.
  const noun = OUTPUT_NOUN[def.behaviorKind] ?? 'What it finds';
  return [`${noun}: ${VALUE_AI_MODE_LABEL.recommend}; ${reviewer ? 'you decide' : `${reviewers || 'any member'} decide${reviewers ? '' : 's'}`}`];
}

function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

function whoCan(viewer: BulkAskViewer): string {
  return viewer.adminNames.length ? `An admin (${viewer.adminNames.join(', ')}) can` : 'An admin can';
}

function fixFor(blocker: Exclude<Blocker, 'checking' | 'no_key'>, blocked: BulkAskAgent[], viewer: BulkAskViewer): BulkAskFix {
  const names = blocked.map(({ def }) => agentPlainName(def));
  const agents = listNames(names);
  const many = names.length > 1;
  const agentsLink = { label: 'Agents', href: AGENTS_SETTINGS_HREF };
  // The Agents page still lists a built-in by its own name (Twins, Nexus); say it, so the fix can be found there.
  const listedAs = blocked.some(({ def }) => agentPlainName(def) !== def.name)
    ? `, where ${many ? 'they are' : 'it is'} listed as ${listNames(blocked.map(({ def }) => def.name))}`
    : '';
  switch (blocker) {
    case 'plan':
      return { text: `This workspace's plan does not include AI. ${whoCan(viewer)} change the plan.` };
    case 'off':
      return viewer.isAdmin
        ? { text: `${agents} ${many ? 'are' : 'is'} off. Switch ${many ? 'them' : 'it'} on under Agents${listedAs}.`, link: agentsLink }
        : { text: `${agents} ${many ? 'are' : 'is'} off. ${whoCan(viewer)} switch ${many ? 'them' : 'it'} on under Agents${listedAs}.` };
    case 'not_bulk':
      return viewer.isAdmin
        ? { text: `${agents} ${many ? 'are' : 'is'} set not to work on many items at once. Change that under Agents${listedAs}.`, link: agentsLink }
        : { text: `${agents} ${many ? 'are' : 'is'} set not to work on many items at once. ${whoCan(viewer)} change that under Agents${listedAs}.` };
    case 'no_values':
      return viewer.isAdmin
        ? { text: `No value is set to use ${agents}. Choose its values under Agents${listedAs}.`, link: agentsLink }
        : { text: `No value is set to use ${agents}. ${whoCan(viewer)} choose its values under Agents${listedAs}.` };
  }
}

/** Which agents the bulk bar offers for the chosen rows, or the one fix when none can be asked. */
export function planBulkAsk(agents: readonly BulkAskAgent[], viewer: BulkAskViewer): BulkAskPlan {
  const candidates = agents.filter(({ def }) => runsOverChosenItems(def));
  const entries: BulkAskEntry[] = [];
  const blocked = new Map<Blocker, BulkAskAgent[]>();
  for (const agent of candidates) {
    const name = agentPlainName(agent.def);
    const blocker = blockerFor(agent, viewer);
    if (blocker) {
      blocked.set(blocker, [...(blocked.get(blocker) ?? []), agent]);
      continue;
    }
    const work = workOf(agent.def);
    const cap = capOf(agent);
    entries.push({
      agentType: agent.def.agentType,
      name,
      label: entryLabel(name, work, viewer.count, cap),
      decides: decidesLines(agent, viewer),
      note: viewer.count > cap ? `It reads at most ${cap} items in one run, the oldest first.` : null,
      work,
      cap,
      def: agent.def,
    });
  }
  if (entries.length > 0) return { entries, fix: null, needsKey: false };
  const first = BLOCKER_ORDER.find((b) => blocked.has(b));
  if (!first || first === 'checking') return { entries, fix: null, needsKey: false };
  if (first === 'no_key') return { entries, fix: null, needsKey: true };
  return { entries, fix: fixFor(first, blocked.get(first) ?? [], viewer), needsKey: false };
}

/** What the bar knew when the run started: how many rows were chosen, and what the agent does with them. */
export interface BulkAskRunContext {
  chosen: number;
  cap: number;
  work: BulkAskWork;
}

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/**
 * The line a person reads when a run ends, from its counts. It says what was
 * proposed, and — when the run read fewer items than were chosen — how many
 * were left out and why, so "for these N" never quietly means fewer.
 */
export function bulkAskResultLine(
  name: string,
  stats: BatchStats | null | undefined,
  status: string,
  run?: BulkAskRunContext,
): string {
  if (status === 'cancelled') return `${name} stopped before it finished.`;
  if (!stats || status === 'failed') return `${name} could not run. Nothing was proposed.`;
  const work = run?.work ?? 'items';
  const read = stats.itemsRead;
  const nothingDone = stats.proposed === 0 && (stats.processed ?? 0) === 0 && stats.skipped === 0;

  if (read === undefined && stats.errors > 0 && nothingDone) return `${name} could not read the chosen items. Nothing was proposed.`;

  const leftOut = run && read !== undefined && run.chosen > read ? run.chosen - read : 0;
  const leftOutLine = leftOut
    ? `${leftOut} of the ${run?.chosen} chosen ${plural(leftOut, 'was', 'were')} not read: ${name} reads only active items${run && run.chosen > run.cap ? `, at most ${run.cap} in one run` : ''}`
    : null;
  const finish = (parts: string[]) => `${[...parts, ...(leftOutLine ? [leftOutLine] : [])].join('. ')}.`;

  if (read === 0) return finish([`${name} had no active item to read. Nothing was proposed`]);
  if (work === 'compare' && read !== undefined && read < 2) {
    return finish([`${name} needs two or more active items among those chosen to compare. Nothing was proposed`]);
  }
  if (stats.errors > 0 && nothingDone) {
    const what =
      work === 'compare'
        ? `could not compare them`
        : work === 'estimates'
          ? `could not make ${plural(stats.errors, 'the estimate', `any of the ${stats.errors} estimates`)}`
          : `could not do ${plural(stats.errors, 'the item', `any of the ${stats.errors}`)}`;
    return finish([`${name} ${what}. Nothing was proposed`]);
  }
  const parts = [
    stats.proposed === 0
      ? `${name} had nothing new to recommend`
      : `${name} made ${stats.proposed} ${stats.proposed === 1 ? 'recommendation; it waits' : 'recommendations; they wait'} for review`,
  ];
  if (stats.skipped > 0) parts.push(work === 'compare' ? `${stats.skipped} had already been proposed` : `${stats.skipped} already had an answer or one waiting`);
  if (stats.errors > 0) {
    parts.push(
      work === 'compare'
        ? `${stats.errors} ${plural(stats.errors, 'comparison', 'comparisons')} could not be done`
        : work === 'estimates'
          ? `${stats.errors} ${plural(stats.errors, 'estimate', 'estimates')} could not be made`
          : `${stats.errors} could not be done`,
    );
  }
  return finish(parts);
}
